import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Calendar, User, ArrowRight, ShoppingBag, FileText, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchData();
    }
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [ordersRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/bookings`, { headers: getAuthHeader() })
      ]);
      setOrders(ordersRes.data.slice(0, 5));
      setBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-[#E5D3B3]/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-[#E5D3B3]" />
              </div>
              <div>
                <h1 className="font-serif text-3xl text-[#F5F2EB]">Welcome back, {user?.name}</h1>
                <p className="text-[#A3988F]">{user?.email}</p>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { name: 'Shop Fashion', icon: ShoppingBag, path: '/fashion', color: '#8C7B6B' },
              { name: 'Nutrition Plans', icon: FileText, path: '/nutrition/meal-plans', color: '#E5D3B3' },
              { name: 'Book Consultation', icon: Calendar, path: '/nutrition/consultations', color: '#FAD6C6' },
              { name: 'My Orders', icon: Package, path: '/orders', color: '#A3988F' },
            ].map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className="luxury-card rounded-xl p-6 flex items-center space-x-4 group"
                  data-testid={`quick-link-${index}`}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <span className="text-[#F5F2EB] group-hover:text-[#E5D3B3] transition-colors">{item.name}</span>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Recent Orders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="luxury-card rounded-xl p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-2xl text-[#F5F2EB]">Recent Orders</h2>
                <Link to="/orders" className="text-sm text-[#E5D3B3] hover:underline" data-testid="view-all-orders">
                  View All
                </Link>
              </div>

              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-[#E5D3B3]/30 mx-auto mb-4" />
                  <p className="text-[#A3988F]">No orders yet</p>
                  <Link to="/fashion" className="text-[#E5D3B3] text-sm hover:underline mt-2 inline-block">
                    Start Shopping
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="flex justify-between items-center p-4 bg-[#261E1D] rounded-lg">
                      <div>
                        <p className="text-[#F5F2EB]">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-sm text-[#A3988F]">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#E5D3B3] font-semibold">${order.total.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.status === 'processing' ? 'bg-[#FAD6C6]/20 text-[#FAD6C6]' :
                          order.status === 'completed' ? 'bg-[#E5D3B3]/20 text-[#E5D3B3]' :
                          'bg-[#A3988F]/20 text-[#A3988F]'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Recent Bookings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="luxury-card rounded-xl p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-2xl text-[#F5F2EB]">Upcoming Bookings</h2>
              </div>

              {bookings.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-[#E5D3B3]/30 mx-auto mb-4" />
                  <p className="text-[#A3988F]">No bookings yet</p>
                  <Link to="/nutrition/consultations" className="text-[#E5D3B3] text-sm hover:underline mt-2 inline-block">
                    Book a Consultation
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="flex justify-between items-center p-4 bg-[#261E1D] rounded-lg">
                      <div>
                        <p className="text-[#F5F2EB]">{booking.service_type.replace(/_/g, ' ')}</p>
                        <div className="flex items-center space-x-2 text-sm text-[#A3988F]">
                          <Clock className="w-4 h-4" />
                          <span>{booking.date} at {booking.time_slot}</span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        booking.status === 'confirmed' ? 'bg-[#E5D3B3]/20 text-[#E5D3B3]' :
                        booking.status === 'pending' ? 'bg-[#FAD6C6]/20 text-[#FAD6C6]' :
                        'bg-[#A3988F]/20 text-[#A3988F]'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
